import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { DuplicateNotice } from "../../components/crm/DuplicateNotice";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import { createCompany, duplicateCandidateId, getCompany, isApiError, updateCompany } from "../../lib/crm-api";
import type { CrmStatus } from "../../lib/crm-types";
import { useConfig } from "../../state/config-context";

function CompanyFormInner({ id }: { id: string | undefined }): ReactNode {
  const { t } = useConfig();
  const navigate = useNavigate();
  const editing = id !== undefined;

  const [name, setName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [taxId, setTaxId] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<CrmStatus>("active");
  const [tagsText, setTagsText] = useState("");
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<{ message: string; candidate: string | null } | null>(
    null,
  );

  useEffect(() => {
    if (id === undefined) {
      return;
    }
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const company = await getCompany(id);
        if (cancelled) {
          return;
        }
        setName(company.name);
        setLegalName(company.legal_name ?? "");
        setTaxId(company.tax_id ?? "");
        setEmail(company.email ?? "");
        setStatus((company.status as CrmStatus) ?? "active");
        setTagsText(company.tags.join(", "));
      } catch (caught) {
        if (!cancelled) {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  const onSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      setSaving(true);
      setError(null);
      setDuplicate(null);
      const tags = tagsText
        .split(",")
        .map((value) => value.trim())
        .filter((value) => value !== "");
      const input = {
        name: name.trim(),
        legal_name: legalName.trim() === "" ? null : legalName.trim(),
        tax_id: taxId.trim() === "" ? null : taxId.trim(),
        email: email.trim() === "" ? null : email.trim().toLowerCase(),
        status,
        tags,
      };
      try {
        const result =
          id === undefined ? await createCompany(input) : await updateCompany(id, input);
        navigate(`/crm/companies/${result.id}`, { replace: true });
      } catch (caught) {
        const candidate = duplicateCandidateId(caught);
        if (candidate !== null && isApiError(caught)) {
          setDuplicate({ message: caught.detail, candidate });
        } else {
          setError(isApiError(caught) ? caught.detail : t("error.network"));
        }
      } finally {
        setSaving(false);
      }
    },
    [email, id, legalName, name, navigate, status, t, tagsText, taxId],
  );

  const title = editing ? t("crm.companies.edit") : t("crm.companies.create");

  return (
    <section className="page" aria-labelledby="crm-company-form-title">
      <header className="page__header">
        <h1 id="crm-company-form-title">{title}</h1>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {duplicate !== null ? (
        <DuplicateNotice
          message={duplicate.message}
          candidateId={duplicate.candidate}
          basePath="/crm/companies"
        />
      ) : null}

      {loading ? (
        <Spinner label={t("app.loading")} />
      ) : (
        <form className="form form--inline-card" onSubmit={(event) => void onSubmit(event)}>
          <div className="form-grid">
            <label className="field" htmlFor="company-name">
              <span>{t("crm.company.name")}</span>
              <input
                id="company-name"
                required
                maxLength={200}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="company-legal-name">
              <span>{t("crm.company.legalName")}</span>
              <input
                id="company-legal-name"
                maxLength={200}
                value={legalName}
                onChange={(event) => setLegalName(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="company-tax-id">
              <span>{t("crm.company.taxId")}</span>
              <input
                id="company-tax-id"
                maxLength={60}
                value={taxId}
                onChange={(event) => setTaxId(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="company-email">
              <span>{t("crm.company.email")}</span>
              <input
                id="company-email"
                type="email"
                maxLength={320}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </label>
            <label className="field" htmlFor="company-status">
              <span>{t("crm.common.status")}</span>
              <select
                id="company-status"
                value={status}
                onChange={(event) => setStatus(event.target.value as CrmStatus)}
              >
                <option value="active">{t("crm.status.active")}</option>
                <option value="inactive">{t("crm.status.inactive")}</option>
                <option value="archived">{t("crm.status.archived")}</option>
              </select>
            </label>
            <label className="field" htmlFor="company-tags">
              <span>{t("crm.company.tags")}</span>
              <input
                id="company-tags"
                value={tagsText}
                aria-describedby="company-tags-hint"
                onChange={(event) => setTagsText(event.target.value)}
              />
              <small id="company-tags-hint" className="field__hint">
                {t("crm.company.tagsHint")}
              </small>
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn--ghost" onClick={() => navigate(-1)}>
              {t("common.cancel")}
            </button>
            <button type="submit" className="btn btn--primary" disabled={saving}>
              {saving ? t("common.saving") : t("common.save")}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

export function CompanyFormPage(): ReactNode {
  const { id } = useParams<{ id: string }>();
  return (
    <RequirePermission permission={id === undefined ? "crm.company.create" : "crm.company.update"}>
      <CompanyFormInner id={id} />
    </RequirePermission>
  );
}
